import { z } from 'zod'
import {
  audiogramConfigSchema,
  audiogramPropsSchema,
  backgroundStyleSchema,
  clipSceneConfigSchema,
  episodeSchema,
  orientationSchema,
  paragraphSchema,
  sceneConfigSchema,
  scenePropsSchema,
  speakerSchema,
  styleSchema,
  textStyleSchema,
  themeConfigSchema,
  transcriptionStyleSchema,
  transitionSchema,
  wordSchema,
} from './schemas'

export const ORIENTATIONS = {
  square: { width: 1080, height: 1080 },
  landscape: { width: 1920, height: 1080 },
  portrait: { width: 1080, height: 1920 },
} as const
export type Orientation = z.infer<typeof orientationSchema>

export type Paragraph = z.infer<typeof paragraphSchema>
export type Word = z.infer<typeof wordSchema>

export interface Speaker extends z.infer<typeof speakerSchema> {}

export interface Episode extends z.infer<typeof episodeSchema> {}

export interface AudiogramConfig extends z.infer<
  typeof audiogramConfigSchema
> {}

export type TextStyle = z.infer<typeof textStyleSchema>

export type TranscriptionStyle = z.infer<typeof transcriptionStyleSchema>

export type BackgroundStyle = z.infer<typeof backgroundStyleSchema>

export type Style = z.infer<typeof styleSchema>

export type SceneConfig = z.infer<typeof sceneConfigSchema>

export type Transition = z.infer<typeof transitionSchema>

export type ClipSceneConfig = z.infer<typeof clipSceneConfigSchema>

export interface ThemeConfig extends z.infer<typeof themeConfigSchema> {}

export interface LayoutProps {
  children: React.ReactNode
  clipScene: ClipSceneConfig
  orientation: Orientation
  speaker?: Speaker | undefined
  style: Style
}

export interface LayoutInfo {
  name: string
  Component: React.FC<LayoutProps>
  defaultScene?: ClipSceneConfig
  defaultStyle: Style
  transcriptions: { [key in Orientation]: Partial<TranscriptionStyle> }
}

// Export schemas and inferred prop types
export {
  audiogramConfigSchema,
  audiogramPropsSchema,
  backgroundStyleSchema,
  clipSceneConfigSchema,
  clipScenePropsSchema,
  episodeSchema,
  orientationSchema,
  paragraphSchema,
  sceneConfigSchema,
  scenePropsSchema,
  speakerSchema,
  styleSchema,
  textStyleSchema,
  themeConfigSchema,
  transcriptionStyleSchema,
  transitionSchema,
  wordSchema,
} from './schemas'

// Re-exported from the JSX-free module so consumers that must not pull the
// `.tsx` layout registry into their type graph (`packages/server`,
// `packages/api`, `apps/trigger`) can take `Layout` from here.
export { LAYOUT_KEYS, type Layout } from './layout'

// Same reason, for the frame-rate constants: `renderChunk` imports `FPS` and
// `clipSceneDurationInFrames` as values to build a chunk's `frameRange`.
export { FPS, clipSceneDurationInFrames } from './video'

export type AudiogramProps = z.infer<typeof audiogramPropsSchema>
export type SceneProps = z.infer<typeof scenePropsSchema>
